import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { submitFeePaymentProofRequest } from "../../redux/feeRelated/feeHandle";

const PAYMENT_METHODS = ["Bank Transfer", "Airtel Money", "TNM Mpamba", "Other"];
const MAX_PROOF_BYTES = 8 * 1024 * 1024;

function termLabel(record) {
    const fs = record?.feeStructure;
    if (!fs) return "School fees";
    const parts = [fs.term, fs.academicYear].filter(Boolean);
    return parts.length ? parts.join(" · ") : "School fees";
}

const StudentFeePaymentDialog = ({
    open,
    onClose,
    studentId,
    records = [],
    paymentSettings,
    onSubmitted,
}) => {
    const payableRecords = useMemo(
        () => records.filter((r) => Number(r.balance) > 0),
        [records],
    );

    const [feeRecordId, setFeeRecordId] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [notes, setNotes] = useState("");
    const [proofFile, setProofFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedRecord = payableRecords.find((r) => (r.id || r._id) === feeRecordId);
    const maxAmount = selectedRecord ? Number(selectedRecord.balance) : 0;
    const settings = paymentSettings || {};
    const hasDetails = Boolean(settings.hasPaymentDetails);

    useEffect(() => {
        if (!open) return;
        setError("");
        setSuccess("");
        if (payableRecords.length === 1 && !feeRecordId) {
            const only = payableRecords[0];
            const id = only.id || only._id;
            setFeeRecordId(id);
            setAmount(String(Number(only.balance) || ""));
        }
    }, [open, payableRecords, feeRecordId]);

    const resetForm = () => {
        setFeeRecordId("");
        setAmount("");
        setPaymentMethod("Bank Transfer");
        setReferenceNumber("");
        setNotes("");
        setProofFile(null);
        setError("");
        setSuccess("");
    };

    const handleClose = () => {
        if (submitting) return;
        resetForm();
        onClose?.();
    };

    const handleSubmit = async () => {
        setError("");
        setSuccess("");

        if (!studentId) {
            setError("Student session expired. Log out and sign in again.");
            return;
        }

        const amt = Number(amount);
        if (!feeRecordId) {
            setError("Choose which fee statement you are paying.");
            return;
        }
        if (!Number.isFinite(amt) || amt <= 0) {
            setError("Enter a valid payment amount.");
            return;
        }
        if (amt > maxAmount) {
            setError(`Amount cannot exceed MWK ${maxAmount.toLocaleString()} outstanding.`);
            return;
        }
        if (!proofFile) {
            setError("Payment proof is required. Upload a photo or PDF of your bank transfer or mobile money receipt.");
            return;
        }
        if (proofFile.size > MAX_PROOF_BYTES) {
            setError("Payment proof must be 8 MB or smaller.");
            return;
        }
        if (!hasDetails) {
            setError("Your school has not published payment details yet.");
            return;
        }

        setSubmitting(true);
        try {
            const result = await submitFeePaymentProofRequest({
                studentId,
                feeRecordId,
                amount: amt,
                paymentMethod,
                referenceNumber: referenceNumber.trim(),
                notes: notes.trim(),
                proofFile,
            });

            if (!result?.ok) {
                setError(result.message || "Could not submit payment.");
                return;
            }

            setSuccess(
                result.data?.message ||
                    "Payment submitted for verification. The school will confirm once they review your proof.",
            );
            onSubmitted?.();
            setTimeout(() => {
                resetForm();
                onClose?.();
            }, 1600);
        } catch (e) {
            setError(e.message || "Could not submit payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle>Make a fee payment</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {success ? (
                        <Alert severity="success" sx={{ borderRadius: 2 }}>
                            {success}
                        </Alert>
                    ) : null}

                    {hasDetails ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Pay to your school account
                            </Typography>
                            {settings.feeBankName ? (
                                <Typography variant="body2">
                                    Bank: <strong>{settings.feeBankName}</strong>
                                    {settings.feeBankBranch ? ` · ${settings.feeBankBranch}` : ""}
                                </Typography>
                            ) : null}
                            {settings.feeAccountNumber ? (
                                <Typography variant="body2">
                                    Account: <strong>{settings.feeAccountNumber}</strong>
                                    {settings.feeAccountHolderName
                                        ? ` (${settings.feeAccountHolderName})`
                                        : ""}
                                </Typography>
                            ) : null}
                            {settings.feeMobileMoneyNumber ? (
                                <Typography variant="body2">
                                    Mobile money: <strong>{settings.feeMobileMoneyNumber}</strong>
                                </Typography>
                            ) : null}
                            {settings.feePaymentInstructions ? (
                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                                    {settings.feePaymentInstructions}
                                </Typography>
                            ) : null}
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Your director has not set up bank payment details yet. Ask the school office before paying.
                        </Alert>
                    )}

                    {payableRecords.length === 0 ? (
                        <Alert severity="success">You have no outstanding fee balance.</Alert>
                    ) : (
                        <>
                            <FormControl fullWidth size="small">
                                <InputLabel>Fee statement</InputLabel>
                                <Select
                                    label="Fee statement"
                                    value={feeRecordId}
                                    onChange={(e) => {
                                        setFeeRecordId(e.target.value);
                                        const rec = payableRecords.find(
                                            (r) => (r.id || r._id) === e.target.value,
                                        );
                                        if (rec) setAmount(String(Number(rec.balance) || ""));
                                    }}
                                >
                                    {payableRecords.map((r) => {
                                        const id = r.id || r._id;
                                        return (
                                            <MenuItem key={id} value={id}>
                                                {termLabel(r)} — MWK {Number(r.balance).toLocaleString()} owed
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Amount paid (MWK)"
                                type="number"
                                size="small"
                                fullWidth
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                helperText={
                                    maxAmount > 0
                                        ? `Outstanding on this statement: MWK ${maxAmount.toLocaleString()}`
                                        : ""
                                }
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Payment method</InputLabel>
                                <Select
                                    label="Payment method"
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                    {PAYMENT_METHODS.map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Transaction / reference number"
                                size="small"
                                fullWidth
                                value={referenceNumber}
                                onChange={(e) => setReferenceNumber(e.target.value)}
                            />

                            <TextField
                                label="Notes (optional)"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />

                            <Box>
                                <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
                                    Payment proof (required) *
                                </Typography>
                                <Button
                                    variant={proofFile ? "contained" : "outlined"}
                                    color={proofFile ? "success" : "primary"}
                                    component="label"
                                    startIcon={<CloudUploadOutlinedIcon />}
                                    fullWidth
                                >
                                    {proofFile ? proofFile.name : "Upload payment proof (photo or PDF) *"}
                                    <input
                                        type="file"
                                        hidden
                                        required
                                        accept="image/*,.pdf,application/pdf"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] || null;
                                            if (file && file.size > MAX_PROOF_BYTES) {
                                                setError("Payment proof must be 8 MB or smaller.");
                                                setProofFile(null);
                                                e.target.value = "";
                                                return;
                                            }
                                            setError("");
                                            setProofFile(file);
                                        }}
                                    />
                                </Button>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Screenshot or photo of your bank transfer or mobile money receipt. Max 8 MB.
                                </Typography>
                            </Box>
                        </>
                    )}

                    {error ? (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {error}
                        </Alert>
                    ) : null}

                    <Typography variant="caption" color="text.secondary">
                        After you submit, the director or bursar will verify your proof and mark the payment on your
                        account.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        submitting ||
                        !hasDetails ||
                        payableRecords.length === 0 ||
                        !proofFile ||
                        Boolean(success)
                    }
                >
                    {submitting ? "Submitting…" : "Submit for verification"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StudentFeePaymentDialog;
